// src/components/Navigation.js
import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap'; // GSAPは引き続き使用
import './Navigation.css'; // 外部CSSファイルを使用

// ★★★ ここに menuItems の定義を追加（または再配置） ★★★
// ナビゲーションメニューのデータ
const menuItems = [
  { id: 'mission', label: 'Mission' },
  { id: 'service', label: 'Service' },
  { id: 'store', label: 'Store' },
  { id: 'company', label: 'Company' },
];
// ★★★ ここまで ★★★

const Navigation = ({ position = 'right' }) => {
  const [activeId, setActiveId] = useState('mission'); // アクティブIDの初期値を'mission'に設定 (ヒーローセクションに対応)
  const listRef = useRef(null); // ul要素の参照
  const dotRef = useRef(null); // ピンクの点（インジケーター）の参照
  const barTopRef = useRef(null); // 上のバーの参照
  const barBottomRef = useRef(null); // 下のバーの参照
  const itemRefs = useRef({}); // 各li要素の参照をIDで管理

  // Intersection Observer によるスクロール時のアクティブセクション判定
  useEffect(() => {
    const observerOptions = {
      root: null, // ビューポートをルート
      rootMargin: '0px',
      threshold: 0.5, // 要素が50%ビューポートに入ったら
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Intersection Observerでアクティブが切り替わったら、そのIDをセット
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    // navItemsではなくmenuItemsを使用
    menuItems.forEach(item => { // ★navItemsをmenuItemsに修正
      const section = document.getElementById(item.id);
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect(); // クリーンアップ
    };
  }, []);

  // インジケーターの状態を更新する中央集権的な関数 (GSAPを使用)
  // activeIdやマウスイベントに応じてこの関数が呼び出される
  const updateIndicators = (targetId, isHover = false) => {
    const targetItem = itemRefs.current[targetId];
    if (!targetItem || !listRef.current || !dotRef.current || !barTopRef.current || !barBottomRef.current) return;

    const navItemsElements = Array.from(listRef.current.children).filter(
      (child) => child.tagName === 'LI'
    );
    const pinkDot = dotRef.current;
    const barTopEl = barTopRef.current;
    const barBottomEl = barBottomRef.current;
    
    // ピンクの円をターゲット要素へ移動
    const dotTop = targetItem.offsetTop + targetItem.offsetHeight / 2 - pinkDot.offsetHeight / 2;
    gsap.to(pinkDot, {
      top: dotTop,
      opacity: 1,
      duration: isHover ? 0.2 : 0.4, // ホバー時は速く、スクロール/クリック時はゆっくり
      ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });

    // バーの太さ（Nav.cssで定義する）
    // const barWidth = 5; // CSSで定義するためJSからは削除

    // 上部バー: 最初のアイテムからターゲットアイテムの上端まで
    gsap.to(barTopEl, {
        top: navItemsElements[0] ? navItemsElements[0].offsetTop : 0, // 最初のliのtopから開始
        height: targetItem.offsetTop - (navItemsElements[0] ? navItemsElements[0].offsetTop : 0),
        opacity: 1,
        duration: isHover ? 0.2 : 0.4,
        ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });

    // 下部バー: ターゲットアイテムの下端から最後のアイテムまで
    gsap.to(barBottomEl, {
        top: targetItem.offsetTop + targetItem.offsetHeight, // ターゲットアイテムの下端から開始
        height: (navItemsElements[navItemsElements.length - 1].offsetTop + navItemsElements[navItemsElements.length - 1].offsetHeight) - (targetItem.offsetTop + targetItem.offsetHeight),
        opacity: 1,
        duration: isHover ? 0.2 : 0.4,
        ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });
    
  };


  // activeIdの変更または初期ロード時にインジケーターを更新
  useEffect(() => {
    // DOMが完全に準備できてから実行するためにsetTimeoutでわずかに遅延
    const timer = setTimeout(() => {
      const initialActiveItem = itemRefs.current[activeId];
      if (initialActiveItem) {
        updateIndicators(activeId); // IDを渡す
      }
    }, 100); 
    return () => clearTimeout(timer);
  }, [activeId]); // activeIdが変更されたら再実行


  // マウスイベントハンドラー
  const handleMouseEnter = (id) => {
    updateIndicators(id, true); // ホバー時
  };

  const handleMouseLeave = () => {
    // マウスがリストから離れたら、アクティブな項目に戻す
    updateIndicators(activeId, false); // ホバー解除時
  };

  const handleClick = (id) => {
    setActiveId(id); // クリックでアクティブIDを変更
    // スムーズスクロール
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`navigation ${position}`} onMouseLeave={handleMouseLeave}> {/* onMouseLeaveをnavに追加 */}
      <ul ref={listRef} className="nav-list">
        {menuItems.map((item) => ( // ★★★ menuItems を使用 ★★★
          <li
            key={item.id}
            ref={(el) => (itemRefs.current[item.id] = el)}
            className={activeId === item.id ? 'active' : ''} // アクティブクラスを追加
            onMouseEnter={() => handleMouseEnter(item.id)}
            onClick={() => handleClick(item.id)}
          >
            <a href={`#${item.id}`}> {/* リンク先をセクションIDに */}
              {item.label}
            </a>
          </li>
        ))}

        {/* ピンクの点とバーのコンテナ */}
        <div className="indicator-container">
            <div ref={dotRef} className="pink-dot"></div>
            <div ref={barTopRef} className="connected-bar bar-top"></div>
            <div ref={barBottomRef} className="connected-bar bar-bottom"></div>
        </div>
      </ul>
    </nav>
  );
};

export default Navigation;